import LockRounded from '@mui/icons-material/LockRounded';
import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { businessDate } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { toOpeningRequest } from './forms';
import { InventoryLayout } from './InventoryLayout';
import { idem, inventoryKeys, todayIn, useLocations } from './model';
import { StockEntryForm } from './StockEntryForm';

export function OpeningStockPage() {
  const { can } = useAuth();
  const { settings, format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const locations = useLocations();
  // Remount the form after each successful entry so it starts clean.
  const [formKey, setFormKey] = useState(0);
  const allowed = can('inventory.opening_stock') && can('products.view_cost');
  const recentQuery = { kind: 'OPENING' as const, page: 1, pageSize: 5 };
  const recent = useQuery({
    queryKey: inventoryKeys.adjustments(recentQuery),
    queryFn: () => unwrap(api.GET('/stock/adjustments', { params: { query: recentQuery } })),
    enabled: allowed,
  });
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  const defaultLocation = locations.data?.find((l) => l.isDefault)?.id ?? '';

  if (!allowed)
    return (
      <InventoryLayout>
        <Card>
          <EmptyState
            icon={<LockRounded />}
            title="Opening stock needs extra access"
            body="Recording opening stock requires the opening-stock and view-cost permissions. Ask the shop owner to grant them."
          />
        </Card>
      </InventoryLayout>
    );

  return (
    <InventoryLayout subtitle="Bring in the stock you already had before using the app — once per item and location.">
      {locations.isLoading ? (
        <Skeleton variant="rounded" height={320} />
      ) : (
        <StockEntryForm
          key={formKey}
          mode="OPENING"
          defaultLocationId={defaultLocation}
          today={today}
          submitLabel="Record opening stock"
          onSubmit={async (values) => {
            try {
              const created = await unwrap(
                api.POST('/stock/opening', {
                  params: { header: idem() },
                  body: toOpeningRequest(values),
                }),
              );
              await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
              notify.success(`Opening stock recorded (${created.number})`);
              setFormKey((k) => k + 1);
            } catch (e) {
              notify.error(e);
              throw e;
            }
          }}
          aside={
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Recent opening entries
                </Typography>
                {recent.isLoading ? (
                  <Stack spacing={1}>
                    <Skeleton />
                    <Skeleton />
                  </Stack>
                ) : recent.data?.data.length ? (
                  <Stack spacing={1.25}>
                    {recent.data.data.map((a) => (
                      <Box
                        key={a.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => void navigate(`/inventory/adjustments/${a.id}`)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && void navigate(`/inventory/adjustments/${a.id}`)
                        }
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          cursor: 'pointer',
                          p: 1,
                          mx: -1,
                          borderRadius: '10px',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {a.number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {a.locationName} · {businessDate(a.businessDate, format)} ·{' '}
                            {a.lines.length} items
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          <Money value={a.totalValue} />
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None yet. Items already having stock history at a location can’t get opening
                    stock there — use an adjustment instead.
                  </Typography>
                )}
              </CardContent>
            </Card>
          }
        />
      )}
    </InventoryLayout>
  );
}
